import type { Category, Store } from './types';

/** The built-in sections, in a typical walk through a supermarket. */
export const DEFAULT_CATEGORIES: { id: string; name: string; emoji: string }[] = [
  { id: 'produce', name: 'Produce', emoji: '🥬' },
  { id: 'bakery', name: 'Bakery & Bread', emoji: '🍞' },
  { id: 'meat', name: 'Meat & Seafood', emoji: '🥩' },
  { id: 'deli', name: 'Deli', emoji: '🧀' },
  { id: 'canned', name: 'Canned & Jarred', emoji: '🥫' },
  { id: 'grains', name: 'Pasta, Rice & Grains', emoji: '🍝' },
  { id: 'baking', name: 'Baking', emoji: '🧁' },
  { id: 'spices', name: 'Spices & Seasonings', emoji: '🧂' },
  { id: 'condiments', name: 'Condiments & Sauces', emoji: '🍯' },
  { id: 'breakfast', name: 'Breakfast & Cereal', emoji: '🥣' },
  { id: 'snacks', name: 'Snacks & Sweets', emoji: '🍿' },
  { id: 'international', name: 'International', emoji: '🌮' },
  { id: 'beverages', name: 'Beverages', emoji: '🧃' },
  { id: 'dairy', name: 'Dairy & Eggs', emoji: '🥛' },
  { id: 'frozen', name: 'Frozen', emoji: '🧊' },
  { id: 'household', name: 'Household & Cleaning', emoji: '🧻' },
  { id: 'personal', name: 'Personal Care', emoji: '🧴' },
  { id: 'health', name: 'Pharmacy & Health', emoji: '💊' },
  { id: 'baby', name: 'Baby', emoji: '🍼' },
  { id: 'pet', name: 'Pet', emoji: '🐾' },
  { id: 'other', name: 'Other', emoji: '🛒' },
];

/** Where things go when nothing knows better (and where a deleted section's items move). */
export const FALLBACK_CATEGORY = 'other';

/** A store's sections in walking order: its own order first, then any it hasn't placed yet. */
export function orderedCategories(store: Pick<Store, 'aisleOrder'> | null | undefined, categories: Category[]): Category[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const out: Category[] = [];
  const seen = new Set<string>();
  for (const id of store?.aisleOrder ?? []) {
    const c = byId.get(id);
    if (c && !seen.has(id)) { out.push(c); seen.add(id); }
  }
  const rest = categories.filter((c) => !seen.has(c.id)).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  return [...out, ...rest];
}

/** Moves one id up or down a list (for keyboard / screen reader reordering). */
export function moveInList<T>(list: T[], index: number, delta: number): T[] {
  const to = index + delta;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [x] = next.splice(index, 1);
  next.splice(to, 0, x);
  return next;
}
